/**
 * InviteByQRCodeModal Component
 * Modal for generating QR code invitations (in-person onboarding)
 *
 * NOTE: Requires 'qrcode.react' library to be installed:
 * npm install qrcode.react
 * Then uncomment the QRCode component import and usage below
 */
import { useState } from 'react';
import { CreateInvitationRequest, InvitationCreated } from '../../api/organizations';
import { XMarkIcon, ClipboardDocumentIcon, CheckIcon, ArrowDownTrayIcon } from './icons';

import { QRCodeSVG } from 'qrcode.react';

interface InviteByQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  availableQuotas: {
    slots: number;
    conversations: number;
    aiRequests: number;
  };
  onInvite: (data: CreateInvitationRequest) => Promise<InvitationCreated>;
  onSuccess: () => void;
}

export default function InviteByQRCodeModal({
  isOpen,
  onClose,
  availableQuotas,
  onInvite,
  onSuccess,
}: InviteByQRCodeModalProps) {
  const [clipboardSlots, setClipboardSlots] = useState(3);
  const [conversations, setConversations] = useState(2);
  const [aiRequests, setAiRequests] = useState(50);
  const [expiryDays, setExpiryDays] = useState<1 | 7 | 14>(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationCreated | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setClipboardSlots(3);
    setConversations(2);
    setAiRequests(50);
    setExpiryDays(7);
    setError(null);
    setInvitation(null);
    setLinkCopied(false);
    onClose();
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (clipboardSlots > availableQuotas.slots) {
      setError(`Cannot allocate more than ${availableQuotas.slots} clipboard slots`);
      return;
    }

    if (conversations > availableQuotas.conversations) {
      setError(`Cannot allocate more than ${availableQuotas.conversations} conversations`);
      return;
    }

    if (aiRequests > availableQuotas.aiRequests) {
      setError(`Cannot allocate more than ${availableQuotas.aiRequests} AI requests`);
      return;
    }

    setLoading(true);

    try {
      // Create invitation with generated email (will be updated when accepted)
      const result = await onInvite({
        email: `qr-invite-${Date.now()}@temp.paraclete.ai`,
        expiry_days: expiryDays,
      });

      setInvitation(result);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!invitation) return;

    try {
      await navigator.clipboard.writeText(invitation.invitation_url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleDownloadQR = () => {
    if (!invitation) return;

    // Get the SVG element
    const svg = document.querySelector('.qr-code-container svg');
    if (!svg) return;

    // Convert to canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);

      // Trigger download
      const link = document.createElement('a');
      link.download = `invitation-qr-${invitation.invitation.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const formatExpiryDate = () => {
    if (!invitation) return '';
    const expiresAt = new Date(invitation.invitation.expires_at);
    return expiresAt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // QR Code generated state
  if (invitation) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-dark-card rounded-2xl shadow-xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border">
            <h2 className="text-xl font-semibold text-white">QR Code Invitation</h2>
            <button
              onClick={handleClose}
              className="text-dark-text-muted hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* QR Code display */}
            <div className="flex flex-col items-center">
              <div className="qr-code-container w-64 h-64 bg-white rounded-lg p-4 flex items-center justify-center">
                <QRCodeSVG
                  value={invitation.invitation_url}
                  size={224}
                  level="H"
                  includeMargin
                />
              </div>

              <p className="mt-4 text-sm text-dark-text text-center">
                Scan with phone to join your organization
              </p>
            </div>

            {/* Invitation link */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Invitation Link:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitation.invitation_url}
                  readOnly
                  className="flex-1 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-4 py-2.5
                           text-sm text-dark-text
                           focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg
                           hover:bg-dark-bg hover:border-accent-blue
                           transition-colors"
                  title="Copy link"
                >
                  {linkCopied ? (
                    <CheckIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="w-5 h-5 text-dark-text" />
                  )}
                </button>
              </div>
            </div>

            {/* Details */}
            <div className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-dark-text-muted">Quotas:</span>
                <span className="text-dark-text">
                  {clipboardSlots} slots, {conversations} conversations, {aiRequests} AI
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-text-muted">Expires:</span>
                <span className="text-dark-text">{formatExpiryDate()} ({expiryDays} days)</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-dark-border">
            <button
              onClick={handleDownloadQR}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white bg-dark-card border border-dark-border
                       rounded-lg hover:bg-dark-bg
                       transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Download QR
            </button>
            <button
              onClick={() => {
                setInvitation(null);
                setLinkCopied(false);
              }}
              className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border
                       rounded-lg hover:bg-dark-bg
                       transition-colors"
            >
              Generate New
            </button>
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-accent-blue to-accent-orange
                       rounded-lg hover:shadow-lg hover:shadow-accent-blue/50
                       transition-all"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-dark-card rounded-2xl shadow-xl w-full max-w-md mx-4">
        <form onSubmit={handleGenerate}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border">
            <h2 className="text-xl font-semibold text-white">Invite Member via QR Code</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-dark-text-muted hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Info box */}
            <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
              <p className="text-sm text-dark-text-muted">
                Generate a QR code for quick member signup. No email required - perfect for in-person invitations.
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Quota allocations */}
            <div>
              <div className="text-sm font-semibold text-dark-text mb-3">Quota Allocations</div>

              <div className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-4 space-y-4">
                {/* Clipboard slots */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    Clipboard Slots
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max={availableQuotas.slots}
                      value={clipboardSlots}
                      onChange={(e) => setClipboardSlots(parseInt(e.target.value) || 1)}
                      className="w-24 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                               text-dark-text
                               focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                    />
                    <span className="text-sm text-dark-text-muted">
                      / {availableQuotas.slots} available
                    </span>
                  </div>
                </div>

                {/* Conversations */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    Conversations
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max={availableQuotas.conversations}
                      value={conversations}
                      onChange={(e) => setConversations(parseInt(e.target.value) || 1)}
                      className="w-24 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                               text-dark-text
                               focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                    />
                    <span className="text-sm text-dark-text-muted">
                      / {availableQuotas.conversations} available
                    </span>
                  </div>
                </div>

                {/* AI requests */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    AI Requests (monthly)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max={availableQuotas.aiRequests}
                      value={aiRequests}
                      onChange={(e) => setAiRequests(parseInt(e.target.value) || 1)}
                      className="w-24 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                               text-dark-text
                               focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                    />
                    <span className="text-sm text-dark-text-muted">
                      / {availableQuotas.aiRequests} available
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Expiration
              </label>
              <div className="flex gap-3">
                {[1, 7, 14].map((days) => (
                  <label
                    key={days}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="expiry"
                      value={days}
                      checked={expiryDays === days}
                      onChange={() => setExpiryDays(days as 1 | 7 | 14)}
                      className="w-4 h-4 text-accent-blue bg-dark-bg border-dark-border
                               focus:ring-2 focus:ring-accent-blue"
                    />
                    <span className="text-sm text-dark-text">
                      {days} {days === 1 ? 'day' : 'days'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-dark-border">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border
                       rounded-lg hover:bg-dark-bg
                       transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-accent-blue to-accent-orange
                       rounded-lg hover:shadow-lg hover:shadow-accent-blue/50
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all"
            >
              {loading ? 'Generating...' : 'Generate QR Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
